import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useCustomerAuth } from '@/lib/auth/auth-context'
import { ssoStartUrl, friendlyError, type CustomerLoginPicker } from '@/lib/auth/auth-http'
import { ATFLogo } from '@/components/assets/ATFLogo'

// Genesis brand conformance pass (docs/decisions/DEPLOYMENT-LOG.md, "Genesis
// brand conformance — portal + console login"). Full Genesis palette
// (charcoal + gold) — operator decision: BOTH the customer portal and the
// operator console use the same charcoal/gold system, no teal and no
// steel-blue anywhere. Surface distinction between the two apps comes from
// the badge text (CUSTOMER PORTAL here vs OPERATOR CONTROL PLANE on the
// console), copy, and hero content — not from a different accent color.
// Design tokens below match .claude/skills/atf-ee/references/genesis.md's
// Design Tokens table exactly.
const GOLD = '#D4B85C'
const GOLD_DIM = 'rgba(212, 184, 92, 0.15)'
const CHARCOAL = '#18171A'
const CARD = '#21201F'
const WHITE = '#F5F4F0'
const GREY_300 = '#B0AFA8'
const GREY_400 = '#6E6D68'
const GREY_500 = '#4A4944'
const DANGER = '#F27B76'

const capabilities = [
  {
    sprite: '/mascot-speed.png',
    title: 'Billing at a glance',
    body: 'See your contract status without waiting on a support ticket',
  },
  {
    sprite: '/mascot-control.png',
    title: 'Manage your licenses',
    body: 'View and download every license issued to your account',
  },
  {
    sprite: '/mascot-deploy.png',
    title: 'Track your usage',
    body: 'Review activity across your instances, on your own schedule',
  },
]

export function PortalLoginPage() {
  return (
    <div className="flex min-h-screen" style={{ backgroundColor: CHARCOAL }}>
      {/* Left side - customer branding panel (hidden on mobile, matching the
          operator login's lg:flex breakpoint). Gold grid pattern lives on
          this main background only (Genesis rule: never on cards). */}
      <div className="relative hidden overflow-hidden lg:flex lg:w-1/2">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(212, 184, 92, 0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(212, 184, 92, 0.02) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />

        <div
          className="absolute -left-32 -top-32 h-[500px] w-[500px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(212, 184, 92, 0.3) 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
        />

        <div
          className="absolute -bottom-48 -right-48 h-[400px] w-[400px] rounded-full"
          style={{
            background: `radial-gradient(circle, ${GOLD_DIM} 0%, transparent 70%)`,
            filter: 'blur(80px)',
          }}
        />

        <div className="relative z-10 flex w-full flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <ATFLogo variant="full" size="lg" style={{ color: GOLD }} />
            <span
              className="ml-1 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide"
              style={{ backgroundColor: GOLD_DIM, color: GOLD }}
            >
              Customer Portal
            </span>
          </div>

          <div className="max-w-lg">
            <div className="mb-8 flex justify-center">
              <img src="/mascot-idle.svg" alt="" className="h-28 w-28 opacity-90" />
            </div>

            <h1
              className="font-display mb-4 font-semibold"
              style={{ color: WHITE, fontSize: '2.6rem', lineHeight: 1.1 }}
            >
              Manage your ATF account
            </h1>
            <p className="mb-10 text-lg font-light" style={{ color: GREY_300 }}>
              Billing, licenses, and usage for your account — in one place,
              whenever you need it.
            </p>

            <div className="space-y-4">
              {capabilities.map(({ sprite, title, body }) => (
                <div
                  key={title}
                  className="flex items-center gap-5 rounded-xl p-5"
                  style={{ backgroundColor: CARD, border: '1px solid rgba(212, 184, 92, 0.12)' }}
                >
                  <img src={sprite} alt="" className="h-16 w-16 flex-shrink-0" />
                  <div>
                    <p className="font-sub text-base font-semibold" style={{ color: WHITE }}>
                      {title}
                    </p>
                    <p className="mt-1 text-sm" style={{ color: GREY_300 }}>
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <p className="text-sm font-light" style={{ color: GREY_400 }}>
            Access is invite-only &middot; Contact your account owner for access
          </p>
        </div>
      </div>

      {/* Right side - sign-in card */}
      <div className="relative flex flex-1 items-center justify-center overflow-hidden p-8">
        <div
          className="absolute inset-0"
          style={{ background: `linear-gradient(90deg, ${GOLD_DIM} 0%, transparent 50%)` }}
        />

        <div
          className="relative z-10 w-full max-w-sm rounded-xl p-6"
          style={{ backgroundColor: CARD, border: '1px solid rgba(212, 184, 92, 0.12)' }}
        >
          {/* Mobile logo */}
          <div className="mb-6 flex items-center justify-center gap-2 lg:hidden">
            <ATFLogo variant="full" size="md" style={{ color: GOLD }} />
          </div>

          <h2 className="font-sub mb-1 text-lg font-semibold" style={{ color: WHITE }}>
            Sign in to your account
          </h2>
          <p className="mb-5 text-sm" style={{ color: GREY_300 }}>
            Access your billing, licenses, and usage.
          </p>

          <PasswordForm />
          <SsoLinks />
        </div>
      </div>
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  backgroundColor: 'rgba(0, 0, 0, 0.3)',
  border: `1px solid ${GREY_500}`,
}

// PasswordForm: ADR-179 P4 (operator decision 2026-08-31) — the account
// field is GONE from this form. Sign-in is email + password only; the
// server auto-detects which account(s) the email verifies against. A
// single verified account signs straight in (unchanged UX). More than one
// verified account (the same email/password pair exists in multiple
// accounts) surfaces the AccountPicker below instead of navigating away —
// no session exists until the customer picks one.
function PasswordForm() {
  const { loginWithPassword, selectAccount } = useCustomerAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [picker, setPicker] = useState<CustomerLoginPicker | null>(null)
  const [selecting, setSelecting] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    // Visible feedback on an empty submit: the native `required` bubbles
    // are transient and easy to miss (2026-08-31 browser walkthrough) --
    // render the same styled error the server path uses instead.
    if (!email.trim() || !password) {
      setError('Enter your email and password.')
      return
    }
    setSubmitting(true)
    try {
      const outcome = await loginWithPassword(email, password)
      if (outcome.kind === 'picker') {
        // No session yet -- render the picker in place of the form instead
        // of navigating away. The password is never needed again after
        // this point (the server already verified it against every
        // candidate), so it is cleared exactly like the success path.
        setPassword('')
        setPicker(outcome.picker)
        return
      }
      // Never keep the password in component state after a submit.
      setPassword('')
      navigate({ to: '/billing' })
    } catch (err) {
      setError(friendlyError(err, 'Invalid email or password.'))
      setPassword('')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleSelect(accountId: string) {
    if (!picker) return
    setError(null)
    setSelecting(accountId)
    try {
      await selectAccount(picker.pickerToken, accountId)
      navigate({ to: '/billing' })
    } catch (err) {
      setError(friendlyError(err, 'That account could not be selected. Please sign in again.'))
      setPicker(null)
    } finally {
      setSelecting(null)
    }
  }

  if (picker) {
    return <AccountPicker picker={picker} selecting={selecting} error={error} onSelect={handleSelect} />
  }

  return (
    <form onSubmit={handleSubmit}>
      <label
        htmlFor="portal-email"
        className="mb-1 block text-sm font-normal"
        style={{ color: GREY_300 }}
      >
        Email
      </label>
      <input
        id="portal-email"
        type="email"
        autoComplete="username"
        spellCheck={false}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        className="mb-1 w-full rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2"
        style={{ ...inputStyle, color: WHITE, ['--tw-ring-color' as string]: GOLD }}
        required
      />
      <p className="mb-3 text-xs" style={{ color: GREY_400 }}>
        The email address your invite was sent to
      </p>

      <label
        htmlFor="portal-password"
        className="mb-1 block text-sm font-normal"
        style={{ color: GREY_300 }}
      >
        Password
      </label>
      <input
        id="portal-password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        className="mb-1 w-full rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2"
        style={{ ...inputStyle, color: WHITE, ['--tw-ring-color' as string]: GOLD }}
        required
      />
      <p className="mb-3 text-xs" style={{ color: GREY_400 }}>
        The password you set when you accepted your invite
      </p>

      {error && (
        <div
          className="mb-3 rounded-lg p-3"
          style={{ backgroundColor: CARD, borderLeft: `3px solid ${DANGER}` }}
        >
          <p className="text-xs" style={{ color: WHITE }}>
            {error}
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || email.trim().length === 0 || password.length === 0}
        className="w-full rounded-lg text-sm font-semibold disabled:opacity-50"
        style={{ backgroundColor: GOLD, color: CHARCOAL, height: '42px' }}
      >
        {submitting ? 'Signing in…' : 'Sign in'}
      </button>
    </form>
  )
}

const ROLE_LABEL: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
}

// AccountPicker (ADR-179 P4): the same email+password verified against
// more than one account. Genesis glass-card list, one row per verified
// account with its role as a chip, one gold "Select" action per row --
// every row performs the SAME action (redeem the picker_token for that
// account), so a single button style per row is sufficient without a
// primary/secondary distinction.
function AccountPicker({
  picker,
  selecting,
  error,
  onSelect,
}: {
  picker: CustomerLoginPicker
  selecting: string | null
  error: string | null
  onSelect: (accountId: string) => void
}) {
  return (
    <div>
      <p className="mb-3 text-sm" style={{ color: GREY_300 }}>
        Your email and password match more than one account. Choose which one to sign in to.
      </p>
      <div className="space-y-2">
        {picker.picker.map((candidate) => (
          <div
            key={candidate.accountId}
            className="flex items-center justify-between gap-3 rounded-lg p-3"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)', border: `1px solid ${GREY_500}` }}
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold" style={{ color: WHITE }}>
                {candidate.accountName}
              </p>
              <span
                className="mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide"
                style={{ backgroundColor: GOLD_DIM, color: GOLD }}
              >
                {ROLE_LABEL[candidate.role] ?? candidate.role}
              </span>
            </div>
            <button
              type="button"
              disabled={selecting !== null}
              onClick={() => onSelect(candidate.accountId)}
              className="flex-shrink-0 rounded-lg px-4 text-sm font-semibold disabled:opacity-50"
              style={{ backgroundColor: GOLD, color: CHARCOAL, height: '36px' }}
            >
              {selecting === candidate.accountId ? 'Signing in…' : 'Select'}
            </button>
          </div>
        ))}
      </div>

      {error && (
        <div
          className="mt-3 rounded-lg p-3"
          style={{ backgroundColor: CARD, borderLeft: `3px solid ${DANGER}` }}
        >
          <p className="text-xs" style={{ color: WHITE }}>
            {error}
          </p>
        </div>
      )}
    </div>
  )
}

// SsoLinks offers the account's configured SSO (OIDC or SAML) as plain
// navigation links, keyed off the SAME account identifier the password form
// uses (ADR-179 §7-A: "password AND per-account SAML 2.0 AND per-account
// OIDC ... offer password + SSO-if-configured"). There is no RPC today to
// discover WHICH method(s) an account has configured (ADR-179 D4 does not
// add one), so both links are always shown; clicking one for an account
// with that method unconfigured surfaces the server's 404 "not configured"
// after the redirect round-trip rather than being hidden client-side. This
// degrades safely: it costs an extra click, never a security decision (the
// server, not this UI, is what decides whether SSO is valid for the
// account — same fail-closed posture as everything else in this portal).
// Restyled as secondary gold-outline buttons per Genesis Component Rules.
function SsoLinks() {
  const [accountRef, setAccountRef] = useState('')

  return (
    <div className="mt-5 pt-4" style={{ borderTop: `1px solid ${GREY_500}` }}>
      <p className="mb-2 text-xs" style={{ color: GREY_400 }}>
        Or sign in with your organization's single sign-on:
      </p>
      <label htmlFor="portal-sso-account" className="sr-only">
        Account for single sign-on
      </label>
      <input
        id="portal-sso-account"
        type="text"
        value={accountRef}
        onChange={(e) => setAccountRef(e.target.value)}
        placeholder="your-company"
        aria-label="Account for single sign-on"
        className="mb-2 w-full rounded-lg px-3 py-2 text-sm outline-none focus:ring-2"
        style={{ ...inputStyle, color: WHITE, ['--tw-ring-color' as string]: GOLD }}
      />
      <div className="flex gap-2">
        <a
          href={accountRef.trim() ? ssoStartUrl('oidc', accountRef.trim()) : undefined}
          aria-disabled={accountRef.trim().length === 0}
          className="flex-1 rounded-lg py-2 text-center text-sm font-medium hover:opacity-80 aria-disabled:pointer-events-none aria-disabled:opacity-40"
          style={{ border: `1px solid ${GOLD}`, color: GOLD }}
        >
          OIDC
        </a>
        <a
          href={accountRef.trim() ? ssoStartUrl('saml', accountRef.trim()) : undefined}
          aria-disabled={accountRef.trim().length === 0}
          className="flex-1 rounded-lg py-2 text-center text-sm font-medium hover:opacity-80 aria-disabled:pointer-events-none aria-disabled:opacity-40"
          style={{ border: `1px solid ${GOLD}`, color: GOLD }}
        >
          SAML
        </a>
      </div>
    </div>
  )
}
